import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Row, Spin } from 'antd';
import { StyledContainer } from '../../../components/container/styled';
import { TextLarge } from '../../../components/text';
import { CardTheme } from '../../../components/card';
import { ContainerButton } from '../../../styles/global-style';
import { ButtonTheme, RenderForm } from '../../../components';
import {
  FORM_WARRANTY_EDIT_PRODUCT_LEADING,
  FORM_WARRANTY_EDIT_PRODUCT_TRAILING,
  getWarrantyEditBrandModelFields,
  getWarrantyEditRowFieldNames,
  FORM_USER_REGISTRATION_WARRANTY,
} from './form';
import { useGetWarrantyEditList } from './hooks/useGetWarrantyEditList';
import { EditExpiredModal } from './modal/EditExpiredModal';

const MAX_BRAND_SETS = 3;

export const WarrantyEdit = () => {
  /** บวก = เพิ่มชุดจากปุ่ม; ลบ = ลบชุด (รวมชุดที่มากับข้อมูลเดิม) — รวมกับ base แล้ว clamp */
  const [mobileSetCountDelta, setMobileSetCountDelta] = useState(0);

  const {
    control,
    setValue,
    getValues,
    channelBuyOptions,
    mobileBrandOptions,
    openEditExpired,
    onToggleEditExpired,
    dataEdit,
    isLoadingWarrantyEdit,
    goBack,
    onSubmit,
    handleSubmit,
    handleOnEnterBarcode,
    isloadingBarcode,
    isloadingToSave,
  } = useGetWarrantyEditList();

  const baseBrandSetCount = useMemo(() => {
    if (!dataEdit) {
      return 1;
    }
    const len = dataEdit?.warrantyData?.mobiles?.length;
    return len > 0 ? len : 1;
  }, [dataEdit]);

  const totalSetCount = Math.max(
    1,
    Math.min(MAX_BRAND_SETS, baseBrandSetCount + mobileSetCountDelta),
  );
  const canAddMoreBrandSets = totalSetCount < MAX_BRAND_SETS;

  useEffect(() => {
    setMobileSetCountDelta(0);
  }, [dataEdit?.warrantyId]);

  const productLeadingWithFirstBrand = useMemo(
    () => [
      ...FORM_WARRANTY_EDIT_PRODUCT_LEADING({ onEnter: handleOnEnterBarcode }),
      ...getWarrantyEditBrandModelFields(0, mobileBrandOptions, { required: true }),
    ],
    [handleOnEnterBarcode, mobileBrandOptions],
  );

  const extraBrandFormBlocks = useMemo(() => {
    const blocks = [];
    for (let i = 1; i < totalSetCount; i += 1) {
      blocks.push(getWarrantyEditBrandModelFields(i, mobileBrandOptions, { required: false }));
    }
    return blocks;
  }, [totalSetCount, mobileBrandOptions]);

  const clearEditBrandRow = useCallback(
    (setIndex) => {
      const { brand, model, warrantyMobileId } = getWarrantyEditRowFieldNames(setIndex);
      setValue(brand, undefined, { shouldDirty: true });
      setValue(model, '', { shouldDirty: true });
      setValue(warrantyMobileId, undefined, { shouldDirty: true });
    },
    [setValue],
  );

  const handleRemoveExtraBrandBlock = useCallback(
    (blockIndex) => {
      const c = totalSetCount;
      if (c <= 1 || blockIndex < 0 || blockIndex > c - 2) return;
      for (let s = blockIndex + 1; s < c - 1; s += 1) {
        const from = getWarrantyEditRowFieldNames(s + 1);
        const to = getWarrantyEditRowFieldNames(s);
        setValue(to.brand, getValues(from.brand), { shouldDirty: true });
        setValue(to.model, getValues(from.model) ?? '', { shouldDirty: true });
        setValue(to.warrantyMobileId, getValues(from.warrantyMobileId), { shouldDirty: true });
      }
      clearEditBrandRow(c - 1);
      setMobileSetCountDelta((d) => d - 1);
    },
    [totalSetCount, getValues, setValue, clearEditBrandRow],
  );

  const productTrailingForms = useMemo(
    () => FORM_WARRANTY_EDIT_PRODUCT_TRAILING({ channelBuyOptions }),
    [channelBuyOptions],
  );

  return (
    <Spin spinning={isLoadingWarrantyEdit || isloadingBarcode || isloadingToSave}>
      <StyledContainer>
        <TextLarge text="แก้ไขข้อมูลลงทะเบียนสินค้า" />
        <Row gutter={16}>
          <Col span={14}>
            <CardTheme>
              <TextLarge text="ลูกค้าลงทะเบียนรับประกัน" />
              <RenderForm
                control={control}
                setValue={setValue}
                getValues={getValues}
                forms={FORM_USER_REGISTRATION_WARRANTY()}
              />
              <ContainerButton right>
                <ButtonTheme useFor="EDIT2" title="แก้ไขวันหมดอายุ" onClick={onToggleEditExpired} />
                {openEditExpired && (
                  <EditExpiredModal
                    open={openEditExpired}
                    onToggle={onToggleEditExpired}
                    dataEdit={dataEdit}
                  />
                )}
              </ContainerButton>
            </CardTheme>
          </Col>
          <Col span={10}>
            <CardTheme>
              <TextLarge text="สินค้าลงทะเบียนรับประกัน" />
              <RenderForm
                control={control}
                setValue={setValue}
                getValues={getValues}
                forms={productLeadingWithFirstBrand}
              />
              {canAddMoreBrandSets && (
                <Button
                  type="dashed"
                  block
                  onClick={() => {
                    setMobileSetCountDelta((d) => {
                      const nextTotal = baseBrandSetCount + d + 1;
                      return nextTotal <= MAX_BRAND_SETS ? d + 1 : d;
                    });
                  }}
                  style={{ marginTop: 4, marginBottom: 8 }}
                >
                  เพิ่มยี่ห้อและรุ่น
                </Button>
              )}
              {extraBrandFormBlocks.map((block, idx) => (
                <div key={`warranty-edit-extra-brand-${idx}`}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                    <Button
                      type="link"
                      danger
                      size="small"
                      onClick={() => handleRemoveExtraBrandBlock(idx)}
                    >
                      ลบชุดนี้
                    </Button>
                  </div>
                  <RenderForm
                    control={control}
                    setValue={setValue}
                    getValues={getValues}
                    forms={block}
                  />
                </div>
              ))}
              <RenderForm
                control={control}
                setValue={setValue}
                getValues={getValues}
                forms={productTrailingForms}
              />
            </CardTheme>
          </Col>
        </Row>
        <Row gutter={16} justify="space-between" align="middle" style={{ marginTop: '2%' }}>
          <Col>
            <ButtonTheme useFor="BACK" title="ย้อนกลับ" onClick={goBack} />
          </Col>
          <Col>
            <ButtonTheme useFor="SUBMIT" title="ลงทะเบียน" onClick={handleSubmit(onSubmit)} />
          </Col>
        </Row>
      </StyledContainer>
    </Spin>
  );
};
